import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import PageHelp from '../components/help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function AvailabilityPage() {
  const { token, user } = useAuth();
  const [availabilityRules, setAvailabilityRules] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Availability form
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  
  // Blackout form
  const [blackoutDate, setBlackoutDate] = useState('');
  const [blackoutReason, setBlackoutReason] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [rulesRes, blackoutsRes] = await Promise.all([
        axios.get(`${API_URL}/api/providers/${user.id}/availability-rules`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/providers/${user.id}/blackout-dates`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAvailabilityRules(rulesRes.data);
      setBlackoutDates(blackoutsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load availability data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAvailability = async (e) => {
    e.preventDefault();
    
    if (!dayOfWeek || !startTime || !endTime) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/dashboard/availability-rules`,
        {
          providerId: user.id,
          dayOfWeek: parseInt(dayOfWeek),
          startTime,
          endTime
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Availability added');
      setDayOfWeek('');
      setStartTime('09:00');
      setEndTime('17:00');
      loadData();
    } catch (error) {
      console.error('Error adding availability:', error);
      toast.error('Failed to add availability');
    }
  };

  const handleDeleteAvailability = async (ruleId) => {
    try {
      await axios.delete(
        `${API_URL}/api/dashboard/availability-rules/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Availability removed');
      loadData();
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error('Failed to delete availability');
    }
  };

  const handleAddBlackout = async (e) => {
    e.preventDefault();
    
    if (!blackoutDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/dashboard/blackout-dates`,
        {
          providerId: user.id,
          date: blackoutDate,
          reason: blackoutReason || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Blackout date added');
      setBlackoutDate('');
      setBlackoutReason('');
      loadData();
    } catch (error) {
      console.error('Error adding blackout:', error);
      toast.error('Failed to add blackout date');
    }
  };

  const handleDeleteBlackout = async (blackoutId) => {
    try {
      await axios.delete(
        `${API_URL}/api/dashboard/blackout-dates/${blackoutId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Blackout date removed');
      loadData();
    } catch (error) {
      console.error('Error deleting blackout:', error);
      toast.error('Failed to delete blackout date');
    }
  };

  const getDayLabel = (dayNum) => {
    return DAYS.find(d => d.value === dayNum)?.label || dayNum;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Weekly Availability */}
        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-1">
            Weekly Availability
            <PageHelp
              testId="availability"
              title="Availability"
              purpose="Set the days and hours you work so clients only book open times."
              actions={[
                { label: 'Add Availability', desc: 'set your working hours for a day of the week' },
                { label: 'Add Blackout Date', desc: 'block a day off, like a holiday or vacation' },
              ]}
              nextStep="Add your weekly hours, then block any days you're off."
            />
          </h2>
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Add Form */}
            <Card>
              <CardHeader>
                <CardTitle>Add Availability</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddAvailability} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                      <SelectTrigger data-testid="day-select">
                        <SelectValue placeholder="Select day" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS.map((day) => (
                          <SelectItem key={day.value} value={day.value.toString()}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startTime">Start Time</Label>
                      <Input
                        id="startTime"
                        data-testid="start-time-input"
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endTime">End Time</Label>
                      <Input
                        id="endTime"
                        data-testid="end-time-input"
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12" data-testid="add-availability-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Availability
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Current Rules */}
            <Card>
              <CardHeader>
                <CardTitle>Current Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                {availabilityRules.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No availability set</p>
                ) : (
                  <div className="space-y-2" data-testid="availability-list">
                    {availabilityRules
                      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                      .map((rule) => (
                        <div
                          key={rule.id}
                          className="flex justify-between items-center p-3 border rounded-lg"
                          data-testid={`availability-rule-${rule.id}`}
                        >
                          <div>
                            <p className="font-medium">{getDayLabel(rule.dayOfWeek)}</p>
                            <p className="text-sm text-muted-foreground">
                              {rule.startTime} - {rule.endTime}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAvailability(rule.id)}
                            data-testid={`delete-rule-${rule.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Blackout Dates */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Blackout Dates</h2>
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Add Form */}
            <Card>
              <CardHeader>
                <CardTitle>Add Blackout Date</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddBlackout} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="blackoutDate">Date</Label>
                    <Input
                      id="blackoutDate"
                      data-testid="blackout-date-input"
                      type="date"
                      value={blackoutDate}
                      onChange={(e) => setBlackoutDate(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="blackoutReason">Reason (optional)</Label>
                    <Textarea
                      id="blackoutReason"
                      data-testid="blackout-reason-input"
                      placeholder="e.g., Vacation, Holiday"
                      value={blackoutReason}
                      onChange={(e) => setBlackoutReason(e.target.value)}
                      rows={2}
                    />
                  </div>

                  <Button type="submit" className="w-full h-12" data-testid="add-blackout-button">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Add Blackout Date
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Current Blackouts */}
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Blackouts</CardTitle>
              </CardHeader>
              <CardContent>
                {blackoutDates.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No blackout dates</p>
                ) : (
                  <div className="space-y-2" data-testid="blackout-list">
                    {blackoutDates
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((blackout) => (
                        <div
                          key={blackout.id}
                          className="flex justify-between items-center p-3 border rounded-lg"
                          data-testid={`blackout-${blackout.id}`}
                        >
                          <div>
                            <p className="font-medium">{blackout.date}</p>
                            {blackout.reason && (
                              <p className="text-sm text-muted-foreground">{blackout.reason}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBlackout(blackout.id)}
                            data-testid={`delete-blackout-${blackout.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
